import React, { ReactNode, useState } from 'react';
import { Box, Button, Heading, Icon, IconType } from 'lucid-components-react';
import ReactHtmlParser from 'react-html-parser';
import { DomElement } from 'htmlparser2';

import { mqMin } from 'theme/base';
import { Text } from 'theme/typography';
import { LucidTheme } from 'types/types';
import Link from 'components/link/link';

type Props = {
  category: string;
  jobs: any[];
  blurb: ReactNode;
};

const removeExtraBrTags = (htmlNode: DomElement) => {
  if (htmlNode.type === 'tag' && htmlNode.name === 'div') {
    if (
      htmlNode.children?.length === 1 &&
      htmlNode.children[0].type === 'tag' &&
      htmlNode.children[0].name === 'br'
    ) {
      return null;
    }
  }
  return undefined;
};

const CareerCategory = ({ category, jobs, blurb }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openJobs, setOpenJobs] = useState<Record<string, boolean>>({});
  return (
    <Box key={category}>
      <button
        className={isOpen ? 'category-is-open' : ''}
        css={(theme: LucidTheme) => ({
          background: 'transparent',
          border: 0,
          borderBottom: '4px solid transparent',
          textAlign: 'left',
          width: '100%',

          paddingTop: theme.space[5],
          paddingBottom: theme.space[5],
          paddingLeft: theme.space[3],

          [mqMin.tablet]: {
            paddingTop: theme.space[7],
            paddingBottom: theme.space[7],
            paddingLeft: theme.space[6]
          },
          position: 'relative',

          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '1px',
            backgroundColor: theme.colors.coolGrey[10]
          },

          '&:hover, &.category-is-open': {
            backgroundColor: theme.colors.coolGrey[0],
            borderBottom: `4px solid ${theme.colors.primary}`,
            cursor: 'pointer'
          }
        })}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Heading
          as="span"
          css={{ position: 'relative' }}
          variant="subheadXXS700"
        >
          {category}
          <Text
            css={{ position: 'absolute', top: '-25px', right: '-10px' }}
            textStyles="labelsM400"
          >
            {jobs.length}
          </Text>
        </Heading>
      </button>
      {isOpen &&
        jobs.map(job => {
          const jobId = job.node.id;
          const jobTitle = job.node.text;
          const jobLocation = job.node.categories.location;
          const jobDescription = job.node.description;
          const jobLists = job.node.lists;
          const jobUrl = job.node.applyUrl;

          return (
            <Box
              key={jobId}
              css={theme => ({
                borderBottom: `1px solid ${theme.colors.coolGrey[10]}`
              })}
              display="flex"
              flexWrap="wrap"
              justifyContent="space-between"
              px={{ default: 3, tablet: 9 }}
              py={{ default: 3, tablet: 7 }}
            >
              <Text
                as="span"
                css={(theme: LucidTheme) => ({
                  marginBottom: theme.space[3]
                })}
                textStyles="bodyM400"
              >
                {jobTitle}
              </Text>
              <Text as="span" textStyles="bodyS400">
                {jobLocation}
              </Text>
              <Box flex="0 1 100%" mt={3}>
                <Button
                  asLink
                  css={(theme: LucidTheme) => ({
                    color: theme.colors.blue[60],
                    fontSize: theme.fontSizes[3],
                    fontWeight: theme.fontWeights.bolder,
                    lineHeight: 1.25,
                    position: 'relative'
                  })}
                  onClick={() => {
                    setOpenJobs(openJobs => {
                      const newStatus = !openJobs[jobId];
                      return {
                        ...openJobs,
                        [jobId]: newStatus
                      };
                    });
                  }}
                >
                  {/* ToDo: i18n */}
                  Job details
                  <span
                    css={(theme: LucidTheme) => ({
                      color: theme.colors.coolGrey[100],
                      position: 'absolute',
                      top: 'calc(50% - 6px)',
                      right: -theme.space[4],
                      fontSize: theme.fontSizes[2],
                      transition: 'transform 0.25s',
                      transform: `rotate(${
                        openJobs[jobId] ? '180deg' : '0deg'
                      })`
                    })}
                  >
                    <Icon type={IconType.CarotDown} />
                  </span>
                </Button>
                {openJobs[jobId] && (
                  <Box
                    css={(theme: LucidTheme) => ({
                      '& > div': {
                        fontSize: theme.fontSizes[3],
                        marginBottom: theme.space[4]
                      }
                    })}
                    maxWidth="580px"
                    mt={6}
                  >
                    {ReactHtmlParser(jobDescription, {
                      transform: removeExtraBrTags
                    })}

                    {jobLists.map((list: any) => (
                      <Box key={list.text} mt={8}>
                        <Text
                          as="span"
                          css={{
                            fontWeight: 700
                          }}
                          textStyles="bodyText"
                        >
                          {list.text}
                        </Text>
                        <ul
                          css={theme => ({
                            listStyle: 'none',
                            marginTop: theme.space[3],
                            marginBottom: theme.space[8],
                            '& > li': {
                              marginBottom: theme.space[3]
                            }
                          })}
                          dangerouslySetInnerHTML={{ __html: list.content! }}
                        />
                      </Box>
                    ))}

                    <Text as="section" textStyles="finePrintM400">
                      {blurb}
                    </Text>

                    <Link asButton newTab primary to={jobUrl}>
                      {/* ToDo: i18n */}
                      Apply
                    </Link>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
    </Box>
  );
};

export default CareerCategory;
